import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp } from '@carbon/icons-react'
import type { Edge, EdgeProps, ReactFlowState } from '@xyflow/react'
import {
  BaseEdge,
  EdgeLabelRenderer,
  getSmoothStepPath,
  useStore,
} from '@xyflow/react'
import { Button } from 'app/_components/Button'
import { createElement, memo } from 'react'
import { twMerge } from 'tailwind-merge'
import { useUpdateEdgeData } from '../hooks/useUpdateElementData'
import type { markerVariants } from '../lib/markers'
import { FlowToolbar } from './FlowToolbar'

export type CustomEdgeType = Edge<{
  hasMarkerStart: boolean
  hasMarkerEnd: boolean
  markerVariant: keyof typeof markerVariants
}>

function getRelativePositionProps(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
) {
  const deltaX = targetX - sourceX
  const deltaY = targetY - sourceY

  const isVertical = Math.abs(deltaY) > Math.abs(deltaX)
  const isReversed = isVertical ? deltaY < 0 : deltaX < 0

  if (isVertical) {
    if (isReversed) {
      return {
        className: 'flex-col-reverse',
        markerStartIcon: ArrowDown,
        markerEndIcon: ArrowUp,
      }
    }

    return {
      className: 'flex-col',
      markerStartIcon: ArrowUp,
      markerEndIcon: ArrowDown,
    }
  }

  if (isReversed) {
    return {
      className: 'flex-row-reverse',
      markerStartIcon: ArrowRight,
      markerEndIcon: ArrowLeft,
    }
  }

  return {
    className: 'flex-row',
    markerStartIcon: ArrowLeft,
    markerEndIcon: ArrowRight,
  }
}

function CustomEdge({ selected, data, ...props }: EdgeProps<CustomEdgeType>) {
  const zoom = useStore((state: ReactFlowState) => state.transform[2])

  const {
    id,
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    markerStart,
    markerEnd,
  } = props

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 0,
    offset: 15,
  })

  const { className, markerStartIcon, markerEndIcon } =
    getRelativePositionProps(sourceX, sourceY, targetX, targetY)

  const updateEdgeData =
    useUpdateEdgeData<NonNullable<CustomEdgeType['data']>>(id)

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerStart={markerStart}
        markerEnd={markerEnd}
      />

      <EdgeLabelRenderer>
        <FlowToolbar
          className={twMerge(
            'nopan nodrag pointer-events-auto absolute',
            selected ? 'flex' : 'hidden',
            className,
          )}
          style={{
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px) scale(${1 / zoom})`,
          }}
        >
          <Button
            title={
              markerStart
                ? 'Pfeil am Anfang entfernen'
                : 'Pfeil am Anfang hinzufügen'
            }
            shape="icon"
            variant="ghost"
            type="button"
            aria-label={
              markerStart
                ? 'Pfeil am Anfang entfernen'
                : 'Pfeil am Anfang hinzufügen'
            }
            className={markerStart ? 'bg-black text-white' : undefined}
            onClick={() => {
              updateEdgeData({
                hasMarkerStart: !(data?.hasMarkerStart ?? false),
                markerVariant: 'default',
              })
            }}
          >
            {createElement(markerStartIcon, { 'aria-hidden': true })}
          </Button>
          <Button
            title={
              markerEnd ? 'Pfeil am Ende entfernen' : 'Pfeil am Ende hinzufügen'
            }
            shape="icon"
            variant="ghost"
            type="button"
            aria-label={
              markerEnd ? 'Pfeil am Ende entfernen' : 'Pfeil am Ende hinzufügen'
            }
            className={markerEnd ? 'bg-black text-white' : undefined}
            onClick={() => {
              updateEdgeData({
                hasMarkerEnd: !(data?.hasMarkerEnd ?? false),
                markerVariant: 'default',
              })
            }}
          >
            {createElement(markerEndIcon, { 'aria-hidden': true })}
          </Button>
        </FlowToolbar>
      </EdgeLabelRenderer>
    </>
  )
}

export default memo(CustomEdge)
